import { Injectable } from '@angular/core';
import { UsersService } from './users.service';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { HubMessageTypeEnum } from '@core/enums/hub-message-type.enum';
import { NotificationsService } from './notifications.service';
import { PermissionsEnum } from '@core/enums/permissions.enum';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
    currentUserPermissions: string[] | null = null;
    private hubConnection?: signalR.HubConnection;
    private _isPermissionsReceived$ = new BehaviorSubject<boolean>(false);
    public connectionId: string = '';

    constructor(
        private usersService: UsersService,
        private msalService: MsalService,
        private notificationsService: NotificationsService
    ) {}

    getPermissions(): void {
        if (!this.currentUserPermissions) {
            this.currentUserPermissions = [];
            this.usersService.getAuthorizeUser().subscribe((data) => {
                this.usersService.setCurrentUser(data);
                this.currentUserPermissions = data.permissions;
                this._isPermissionsReceived$.next(true);
                this.startConnection();
            });
        }
    }

    get isPermissionsReceived(): Observable<boolean> {
        return this._isPermissionsReceived$.asObservable();
    }

    resetPermissions(): void {
        this.currentUserPermissions = null;
    }

    isAllowedBySomePermissions(
        action: PermissionsEnum | PermissionsEnum[]
    ): boolean {
        if (Array.isArray(action)) {
            return !!(
                this.currentUserPermissions?.length &&
                action.some((actionItem) =>
                    this.currentUserPermissions?.includes(actionItem)
                )
            );
        } else {
            return !!(
                this.currentUserPermissions?.length &&
                this.currentUserPermissions.includes(action)
            );
        }
    }

    isAllowedByAllPermissions(
        action: PermissionsEnum | PermissionsEnum[]
    ): boolean {
        if (Array.isArray(action)) {
            return !!(
                this.currentUserPermissions?.length &&
                action.every((actionItem) =>
                    this.currentUserPermissions?.includes(actionItem)
                )
            );
        } else {
            return !!(
                this.currentUserPermissions?.length &&
                this.currentUserPermissions.includes(action)
            );
        }
    }

    isAllowedFormFieldsByPermissionsExist(
        action: PermissionsEnum | PermissionsEnum[]
    ): boolean {
        if (!this.currentUserPermissions?.length) {
            return false;
        }

        if (Array.isArray(action)) {
            const allowedPermissions = this.currentUserPermissions.filter(
                (permission) => action.includes(permission as PermissionsEnum)
            );
            return (
                !!allowedPermissions?.length &&
                !!allowedPermissions.find(
                    (allowedPermission) =>
                        this.currentUserPermissions?.filter((permission) =>
                            permission.includes(`${allowedPermission}.`)
                        )?.length
                )
            );
        } else {
            const allowedPermission = this.currentUserPermissions.find(
                (permission) => permission === action
            );
            return (
                !!allowedPermission &&
                !!this.currentUserPermissions.filter((permission) =>
                    permission.includes(`${action}.`)
                )?.length
            );
        }
    }

    private startConnection = () => {
        var request = {
            scopes: [`api://${environment.azureAd.clientId}/access_as_user`],
        };

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${environment.apiUrl}/notificationHub`, {
                accessTokenFactory: () => {
                    //console.log(`[${new Date()}] Acquire Token Silent Started`);
                    return this.msalService.instance
                        .acquireTokenSilent(request)
                        .then((tokenResponse) => {
                            //console.log(`[${new Date()}] Acquire Token Silent Finished`);
                            return Promise.resolve(tokenResponse.accessToken);
                        })
                        .catch(async (error) => {
                            //console.log(`[${new Date()}] Acquire Token Silent error: ` + error);
                            // P.Bruyaka: Commented out because we dont need to use redirect to acquire token for SignalR. Just return empty string.
                            // if (error instanceof InteractionRequiredAuthError) {
                            //     // fallback to interaction when silent call fails
                            //     return this.msalService.instance.acquireTokenRedirect(
                            //         request
                            //     );
                            // }
                            return Promise.resolve('');
                        });
                },
            })
            .withAutomaticReconnect([
                0, 10000, 30000, 60000, 120000, 180000, 240000, 300000,
            ])
            .build();
        this.hubConnection
            .start()
            .then(() => {
                this.registerMessage();
            })
            .catch((err) =>
                console.log(
                    `[${new Date()}] Hub Connection error on start: ` + err
                )
            );
    };

    registerMessage() {
        this.hubConnection?.on(HubMessageTypeEnum.Notification, (message) => {
            this.notificationsService.addNotification(JSON.parse(message));
        });
    }

    closeConnection() {
        this.hubConnection?.stop();
    }
}
